import React from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';

interface Question {
  id: number;
  question_img_url: string;
  has_multiple_answer: boolean;
  duration: number;
  option_count: number;
  exam_id: number;
  subject_id: number;
  chapter_name?: string;
  chapter_id?: number;
  level?: string;
  topic_id?: number;
  correct_option: string;
  solution?: string;
  question_order?: number;
  user_answer?: string;
  score?: number;
  time_taken?: number;
  attempt_date?: string;
  is_correct?: boolean;
  status?: 'correct' | 'incorrect' | 'unanswered';
  explanation?: string;
  options?: { [key: string]: string };
  question_text?: string;
  correct_answer?: string;
}

interface QuizDetails {
  id: number;
  name: string;
  description: string;
  subject_name?: string;
  exam_name?: string;
  exam_id?: number;
}

interface QuizResultsNavState {
  quizReference: string;
  quizDetails: QuizDetails;
  questions: Question[];
  questionStates: any[];
  totalQuestions: number;
}

const QuizResults: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  

  // Get quiz results data from navigation state
  const resultsData = location.state as QuizResultsNavState;

  // Redirect to dashboard if no results data
  if (!resultsData) {
    navigate('/dashboard');
    return null;
  }
  
  // Merge questions with user answers from questionStates
  const questionsWithAnswers = resultsData.questions.map((question, index) => {
    const userState = resultsData.questionStates[index] || {};
    
    // Try different possible property names for user answer
    const userAnswer = userState.selectedAnswer || 
                      userState.answer || 
                      userState.selected_option ||
                      userState.user_answer ||
                      userState.selectedOption;
    
    const isAnswered = userState.answered || 
                      userState.isAnswered || 
                      !!userAnswer ||
                      userState.selected_option !== undefined;
    
    return {
      ...question,
      user_answer: userAnswer,
      is_correct: userState.isCorrect,
      time_taken: userState.timeTaken || userState.timeSpent || 0,
      status: userState.isCorrect ? 'correct' : 
              isAnswered ? 'incorrect' : 'unanswered',
      score: userState.isCorrect ? 4 : isAnswered ? -1 : 0
    };
  });

  // Calculate stats from the merged questionsWithAnswers data
  const totalQuestions = resultsData.totalQuestions;
  const answeredQuestions = questionsWithAnswers.filter(q => q.status !== 'unanswered').length;
  const correctAnswers = questionsWithAnswers.filter(q => q.status === 'correct').length;
  const incorrectAnswers = questionsWithAnswers.filter(q => q.status === 'incorrect').length;
  const unansweredQuestions = questionsWithAnswers.filter(q => q.status === 'unanswered').length;
  const percentage = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
  const totalScore = correctAnswers * 4 - incorrectAnswers * 1;
  const totalTimeSpent = questionsWithAnswers.reduce((sum, q) => sum + (q.time_taken || 0), 0);
  const avgTimePerQuestion = totalQuestions > 0 ? Math.round(totalTimeSpent / totalQuestions) : 0;
  
  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  
  // Get performance grade based on score
  const getPerformanceGrade = (percentage: number) => {
    if (percentage >= 90) return { grade: 'A+', color: '#28a745', message: 'Outstanding!' };
    if (percentage >= 80) return { grade: 'A', color: '#20c997', message: 'Excellent!' };
    if (percentage >= 70) return { grade: 'B+', color: '#17a2b8', message: 'Very Good!' };
    if (percentage >= 60) return { grade: 'B', color: '#6f42c1', message: 'Good!' };
    if (percentage >= 50) return { grade: 'C', color: '#fd7e14', message: 'Average' };
    return { grade: 'D', color: '#dc3545', message: 'Needs Improvement' };
  };
  
  const performanceGrade = getPerformanceGrade(percentage);
  
  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8f9ff', paddingTop: '0px' }}>
      <div className="container py-5">
        {/* Header */}
        <div className="text-center mb-5">
          <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
            style={{ 
              width: '100px', 
              height: '100px',
              background: 'linear-gradient(135deg, #667eea, #764ba2)'
            }}>
            <i className="fas fa-chart-line text-white fa-3x"></i>
          </div>
          <h1 className="display-4 fw-bold mb-3">Quiz Results</h1>
          <p className="lead text-muted">
            {resultsData.quizDetails?.exam_name && `${resultsData.quizDetails.exam_name.toUpperCase()} - `}
            {resultsData.quizDetails?.subject_name} • {resultsData.quizDetails?.name}
          </p>
        </div>

        {/* Performance Overview Cards */}
        <div className="row g-4 mb-5">
          {/* Overall Score */}
          <div className="col-lg-3 col-md-6">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '20px' }}>
              <div className="card-body text-center p-4">
                <div className="position-relative d-inline-flex align-items-center justify-content-center mb-3"
                  style={{ width: '80px', height: '80px' }}>
                  <svg className="position-absolute" width="80" height="80">
                    <circle cx="40" cy="40" r="35" stroke="#e9ecef" strokeWidth="6" fill="none" />
                    <circle
                      cx="40" cy="40" r="35" stroke={performanceGrade.color} strokeWidth="6" fill="none"
                      strokeDasharray="220" strokeDashoffset={220 - (220 * percentage / 100)}
                      strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s ease' }}
                    />
                  </svg>
                  <span className="fw-bold fs-5" style={{ color: performanceGrade.color }}>
                    {percentage}%
                  </span>
                </div>
                <h5 className="fw-bold mb-1">Overall Accuracy</h5>
                <p className="text-muted mb-2">{performanceGrade.message}</p>
                <span className="badge px-3 py-2" style={{ 
                  backgroundColor: performanceGrade.color + '15', 
                  color: performanceGrade.color,
                  borderRadius: '25px'
                }}>
                  Grade: {performanceGrade.grade}
                </span>
              </div>
            </div>
          </div>

          {/* Final Score */}
          <div className="col-lg-3 col-md-6">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '20px' }}>
              <div className="card-body text-center p-4">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                  style={{ 
                    width: '70px', 
                    height: '70px',
                    backgroundColor: '#28a74515',
                    border: '2px solid #28a745'
                  }}>
                  <i className="fas fa-trophy text-success fa-2x"></i>
                </div>
                <h3 className="fw-bold mb-1 text-success">{totalScore}</h3>
                <h6 className="fw-semibold mb-2">Final Score</h6>
                <small className="text-muted">
                  +{correctAnswers * 4} correct, -{incorrectAnswers} incorrect
                </small>
              </div>
            </div>
          </div>

          {/* Time Performance */}
          <div className="col-lg-3 col-md-6">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '20px' }}>
              <div className="card-body text-center p-4">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                  style={{ 
                    width: '70px', 
                    height: '70px',
                    backgroundColor: '#17a2b815',
                    border: '2px solid #17a2b8'
                  }}>
                  <i className="fas fa-clock text-info fa-2x"></i>
                </div>
                <h3 className="fw-bold mb-1 text-info">{formatTime(totalTimeSpent)}</h3>
                <h6 className="fw-semibold mb-2">Time Taken</h6>
                <small className="text-muted">
                  Avg: {formatTime(avgTimePerQuestion)}/question
                </small>
              </div>
            </div>
          </div>

          {/* Questions Attempted */}
          <div className="col-lg-3 col-md-6">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '20px' }}>
              <div className="card-body text-center p-4">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                  style={{ 
                    width: '70px', 
                    height: '70px',
                    backgroundColor: '#6f42c115',
                    border: '2px solid #6f42c1'
                  }}>
                  <i className="fas fa-check text-purple fa-2x"></i>
                </div>
                <h3 className="fw-bold mb-1" style={{ color: '#6f42c1' }}>
                  {answeredQuestions}/{totalQuestions}
                </h3>
                <h6 className="fw-semibold mb-2">Attempted</h6>
                <small className="text-muted">
                  {unansweredQuestions} unanswered
                </small>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4">
          {/* Detailed Breakdown */}
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
              <div className="card-header bg-transparent border-0 p-4">
                <h5 className="fw-bold mb-0">
                  <i className="fas fa-chart-bar me-2 text-primary"></i>
                  Detailed Performance Analysis
                </h5>
              </div>
              <div className="card-body p-4">
                {/* Question-wise Performance */}
                <div className="row g-3 mb-4">
                  <div className="col-md-3">
                    <div className="text-center p-3 bg-success bg-opacity-10 rounded">
                      <div className="text-success fw-bold fs-4">{correctAnswers}</div>
                      <div className="small text-success">Correct</div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-center p-3 bg-danger bg-opacity-10 rounded">
                      <div className="text-danger fw-bold fs-4">{incorrectAnswers}</div>
                      <div className="small text-danger">Incorrect</div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-center p-3 bg-warning bg-opacity-10 rounded">
                      <div className="text-warning fw-bold fs-4">{unansweredQuestions}</div>
                      <div className="small text-warning">Unanswered</div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-center p-3 bg-info bg-opacity-10 rounded">
                      <div className="text-info fw-bold fs-4">{totalQuestions}</div>
                      <div className="small text-info">Total</div>
                    </div>
                  </div>
                </div>

                {/* Subject-wise Performance */}
                <h6 className="fw-semibold mb-3">Performance Analysis</h6>
                <div className="mb-4">
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="fw-medium">Overall Performance</span>
                      <span className="text-muted">{percentage}%</span>
                    </div>
                    <div className="progress" style={{ height: '8px' }}>
                      <div 
                        className="progress-bar bg-primary" 
                        style={{ width: `${percentage}%`, transition: 'width 1s ease' }}
                      ></div>
                    </div>
                  </div>
                  {resultsData.quizDetails?.subject_name && (
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="fw-medium text-capitalize">{resultsData.quizDetails.subject_name}</span>
                        <span className="text-muted">{percentage}%</span>
                      </div>
                      <div className="progress" style={{ height: '8px' }}>
                        <div 
                          className="progress-bar bg-success" 
                          style={{ width: `${percentage}%`, transition: 'width 1s ease' }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Recommendations */}
                <div className="alert border-0" style={{ 
                  backgroundColor: '#667eea15', 
                  borderRadius: '15px',
                  border: '1px solid #667eea30'
                }}>
                  <h6 className="fw-bold text-primary mb-2">
                    <i className="fas fa-lightbulb me-2"></i>
                    Recommendations for Improvement
                  </h6>
                  <ul className="mb-0 small">
                    {percentage < 70 && (
                      <li>Focus more on conceptual understanding rather than rote learning</li>
                    )}
                    {unansweredQuestions > totalQuestions * 0.2 && (
                      <li>Practice time management to attempt more questions</li>
                    )}
                    {avgTimePerQuestion > 90 && (
                      <li>Work on solving questions faster through regular practice</li>
                    )}
                    <li>Review incorrect answers to understand your mistakes</li>
                    <li>Take more mock tests to improve performance consistency</li>
                  </ul>
                </div>

                {/* Question Review Section */}
                {questionsWithAnswers && questionsWithAnswers.length > 0 && (
                  <div className="mt-4">
                    <h6 className="fw-semibold mb-3">
                      <i className="fas fa-list-ul me-2"></i>
                      Question Review ({questionsWithAnswers.length} questions)
                    </h6>
                    
                    <div className="row g-3">
                      {questionsWithAnswers.map((question, index) => {
                        // Construct full image URL if needed
                        const imageUrl = question.question_img_url;
                        return (
                        <div key={question.id} className="col-md-6 col-lg-4">
                          <div className={`card border-0 shadow-sm h-100 ${
                            question.status === 'correct' ? 'border-success' : 
                            question.status === 'incorrect' ? 'border-danger' : 'border-warning'
                          }`} style={{ borderRadius: '12px', borderWidth: '2px !important' }}>
                            <div className="card-body p-3">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <span className="badge" style={{
                                  backgroundColor: question.status === 'correct' ? '#28a745' :
                                                  question.status === 'incorrect' ? '#dc3545' : '#ffc107',
                                  color: 'white',
                                  borderRadius: '12px'
                                }}>
                                  Q{question.question_order || index + 1}
                                </span>
                                <small className="text-muted">
                                  {question.time_taken ? formatTime(question.time_taken) : '--'}
                                </small>
                              </div>
                              <div className="text-center mb-2" style={{ minHeight: '120px' }}>
                                {imageUrl ? (
                                  <div className="position-relative">
                                    <img 
                                      src={imageUrl} 
                                      alt={`Question ${index + 1}`}
                                      className="img-fluid rounded border"
                                      style={{ 
                                        maxHeight: '100px', 
                                        maxWidth: '100%',
                                        objectFit: 'contain',
                                        backgroundColor: '#f8f9fa'
                                      }}
                                      onError={(e) => {
                                        console.error('Failed to load image:', imageUrl);
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                        const parent = target.parentElement;
                                        if (parent) {
                                          parent.innerHTML = `
                                            <div class="bg-light p-3 rounded text-muted border border-dashed">
                                              <i class="fas fa-image-slash fa-2x mb-2"></i>
                                              <div class="small">Image not available</div>
                                              <div class="smaller text-truncate" style="max-width: 150px;">${imageUrl}</div>
                                            </div>
                                          `;
                                        }
                                      }}
                                      onLoad={() => {
                                        console.log('Image loaded successfully:', imageUrl);
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <div className="bg-light p-3 rounded text-muted border border-dashed">
                                    <i className="fas fa-image fa-2x mb-2"></i>
                                    <div className="small">No question image</div>
                                  </div>
                                )}
                              </div>
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="small">
                                  <div>Your: <strong>{question.user_answer || 'Not answered'}</strong></div>
                                  <div>Correct: <strong>{question.correct_option}</strong></div>
                                </div>
                                <div className="text-end">
                                  <div className={`fw-bold ${
                                    question.status === 'correct' ? 'text-success' :
                                    question.status === 'incorrect' ? 'text-danger' : 'text-warning'
                                  }`}>
                                    {question.score || 0} pts
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions and Next Steps */}
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
              <div className="card-header bg-transparent border-0 p-4">
                <h5 className="fw-bold mb-0">
                  <i className="fas fa-rocket me-2 text-primary"></i>
                  What's Next?
                </h5>
              </div>
              <div className="card-body p-4">
                <div className="d-grid gap-3">
                  <Link
                    to="/dashboard"
                    className="btn btn-outline-primary btn-lg"
                    style={{ borderRadius: '15px' }}
                  >
                    <i className="fas fa-home me-2"></i>
                    Back to Dashboard
                  </Link>
                  
                  <Link
                    to="/custom-quiz-generator"
                    className="btn btn-outline-info btn-lg"
                    style={{ borderRadius: '15px' }}
                  >
                    <i className="fas fa-redo me-2"></i>
                    Take Another Quiz
                  </Link>
                  
                  <Link
                    to={`/quiz-results-detailed/${resultsData.quizReference}`}
                    state={resultsData}
                    className="btn btn-outline-success btn-lg"
                    style={{ borderRadius: '15px' }}
                  >
                    <i className="fas fa-chart-line me-2"></i>
                    View Detailed Analytics
                  </Link>
                  
                  <button
                    className="btn btn-outline-secondary btn-lg"
                    style={{ borderRadius: '15px' }}
                    onClick={() => window.print()}
                  >
                    <i className="fas fa-print me-2"></i>
                    Print Results
                  </button>
                </div>

                {/* Performance Insights */}
                <div className="mt-4 p-3" style={{ 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: '15px' 
                }}>
                  <h6 className="fw-bold mb-2">
                    <i className="fas fa-chart-pie me-2 text-warning"></i>
                    Quick Insights
                  </h6>
                  <ul className="small mb-0 list-unstyled">
                    <li className="mb-1">
                      <i className="fas fa-circle text-success me-2" style={{ fontSize: '8px' }}></i>
                      Subject: {resultsData.quizDetails?.subject_name || 'General'}
                    </li>
                    <li className="mb-1">
                      <i className="fas fa-circle text-warning me-2" style={{ fontSize: '8px' }}></i>
                      Time per Q: {formatTime(avgTimePerQuestion)}
                    </li>
                    <li className="mb-1">
                      <i className="fas fa-circle text-info me-2" style={{ fontSize: '8px' }}></i>
                      Attempt Rate: {Math.round((answeredQuestions/totalQuestions)*100)}%
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizResults;
